import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '../../../redux/editorSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { ImageItem } from './ImageSlider.types';

export interface ImageSliderLogicReturn {
  // Translated props
  sliderTitle: string;
  showTitle: boolean;
  titleFontSize: string;
  titleFontWeight: string;
  titleColor: string;
  sliderHeight: string;
  slidesToShow: number;
  autoSlide: boolean;
  autoSlideInterval: number;
  infiniteSlide: boolean;
  showNavigation: boolean;
  showIndicators: boolean;
  imageObjectFit: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  images: ImageItem[];
  
  // State
  currentIndex: number;
  translateX: number;
  slideWidth: number;
  isTransitioning: boolean;
  isEditingTitle: boolean;
  titleEditValue: string;
  touchStart: number | null;
  touchEnd: number | null;
  isMobile: boolean;
  isTablet: boolean;
  extendedImages: ImageItem[];
  
  // Refs
  sliderRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  autoSlideTimer: React.MutableRefObject<NodeJS.Timeout | null>;
  
  // Computed styles
  titleStyle: React.CSSProperties;
  sliderContainerStyle: React.CSSProperties;
  sliderStyle: React.CSSProperties;
  
  // Functions
  goToNext: () => void;
  goToPrev: () => void;
  goToSlide: (index: number) => void;
  startAutoSlide: () => void;
  stopAutoSlide: () => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleTitleDoubleClick: () => void;
  handleTitleBlur: () => void;
  handleImageClick: (image: ImageItem) => void;
  setTitleEditValue: (value: string) => void;
  setIsTransitioning: (value: boolean) => void;
  setCurrentIndex: (value: number) => void;
  setTranslateX: (value: number) => void;
  setSlideWidth: (value: number) => void;
  
  // Utils
  t: (key: string) => string;
  dispatch: any;
  id: string;
  data: any;
  style: React.CSSProperties;
  className: string;
  swipeThreshold: number;
}

export const useImageSliderLogic = (props: any): ImageSliderLogicReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Extract adapted props
  const { style = {}, className = '', id, data = {} } = props;
  
  // Extract data with defaults
  const {
    sliderTitle = '이미지 슬라이더',
    showTitle = false,
    titleFontSize = '24px',
    titleFontWeight = '600',
    titleColor = '#333',
    sliderHeight = '400px',
    slidesToShow = 1,
    autoSlide = true,
    autoSlideInterval = 4000,
    infiniteSlide = true,
    showNavigation = true,
    showIndicators = true,
    imageObjectFit = 'cover',
    images = []
  } = data;

  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Refs
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoSlideTimer = useRef<NodeJS.Timeout | null>(null);
  const swipeThreshold = 50;
  
  // Extended images for infinite slide
  const extendedImages = useMemo(() => {
    if (!images || images.length === 0) return [];
    if (!infiniteSlide) return images;
    
    const lastImages = images.slice(-slidesToShow);
    const firstImages = images.slice(0, slidesToShow);
    return [...lastImages, ...images, ...firstImages];
  }, [images, infiniteSlide, slidesToShow]);
  
  // Computed styles
  const titleStyle = useMemo(() => ({
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    color: titleColor
  }), [titleFontSize, titleFontWeight, titleColor]);
  
  const sliderContainerStyle = useMemo(() => {
    const filteredStyle = { ...style };
    delete filteredStyle.padding;
    delete filteredStyle.paddingTop;
    delete filteredStyle.paddingRight;
    delete filteredStyle.paddingBottom;
    delete filteredStyle.paddingLeft;
    
    return {
      height: sliderHeight,
      background: 'transparent',
      backgroundColor: 'transparent',
      ...filteredStyle
    };
  }, [sliderHeight, style]);
  
  const sliderStyle = useMemo(() => ({
    transform: `translateX(${translateX}px)`,
    transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
    display: 'flex',
    width: 'auto'
  }), [translateX, isTransitioning]);

  // Navigation functions
  const goToNext = useCallback(() => {
    if (!extendedImages || extendedImages.length === 0 || slideWidth === 0) return;
    
    setTranslateX(prev => prev - slideWidth);
    setCurrentIndex(prev => prev + 1);
  }, [extendedImages, slideWidth]);

  const goToPrev = useCallback(() => {
    if (!extendedImages || extendedImages.length === 0 || slideWidth === 0) return;
    
    setTranslateX(prev => prev + slideWidth);
    setCurrentIndex(prev => prev - 1);
  }, [extendedImages, slideWidth]);

  const goToSlide = useCallback((index: number) => {
    if (slideWidth === 0) return;
    
    if (!infiniteSlide) {
      setTranslateX(-index * slideWidth);
      setCurrentIndex(index);
    } else {
      const targetIndex = index + slidesToShow;
      setTranslateX(-targetIndex * slideWidth);
      setCurrentIndex(targetIndex);
    }
  }, [infiniteSlide, slidesToShow, slideWidth]);

  // Auto slide functions
  const startAutoSlide = useCallback(() => {
    if (autoSlide && autoSlideInterval > 0) {
      autoSlideTimer.current = setInterval(() => {
        goToNext();
      }, autoSlideInterval);
    }
  }, [autoSlide, autoSlideInterval, goToNext]);

  const stopAutoSlide = useCallback(() => {
    if (autoSlideTimer.current) {
      clearInterval(autoSlideTimer.current);
      autoSlideTimer.current = null;
    }
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null);
    stopAutoSlide();
  }, [stopAutoSlide]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);
  
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      startAutoSlide();
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > swipeThreshold;
    const isRightSwipe = distance < -swipeThreshold;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
    startAutoSlide();
  }, [touchStart, touchEnd, goToNext, goToPrev, startAutoSlide, swipeThreshold]);

  // Mouse handlers
  const handleMouseEnter = useCallback(() => {
    stopAutoSlide();
  }, [stopAutoSlide]);

  const handleMouseLeave = useCallback(() => {
    startAutoSlide();
  }, [startAutoSlide]);

  // Title edit handlers
  const handleTitleDoubleClick = useCallback(() => {
    setIsEditingTitle(true);
    setTitleEditValue(sliderTitle);
  }, [sliderTitle]);
  
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    
    dispatch(updateComponent({
      id,
      changes: {
        componentProps: {
          ...data,
          sliderTitle: titleEditValue
        }
      }
    }));
  }, [id, data, dispatch, titleEditValue]);

  // Image click handler
  const handleImageClick = useCallback((image: ImageItem) => {
    if (image.link) {
      if (image.openInNewTab) {
        window.open(image.link, '_blank');
      } else {
        window.location.href = image.link;
      }
    }
  }, []);

  // Device detection effect
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };
    
    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    
    return () => {
      window.removeEventListener('resize', checkDeviceType);
    };
  }, []);

  // Slide width calculation effect
  useEffect(() => {
    if (containerRef.current && extendedImages.length > 0) {
      const calculateWidths = () => {
        try {
          const containerStyles = window.getComputedStyle(containerRef.current!);
          const paddingLeft = parseFloat(containerStyles.paddingLeft);
          const paddingRight = parseFloat(containerStyles.paddingRight);
          
          const containerWidth = containerRef.current!.offsetWidth;
          const innerWidth = containerWidth - paddingLeft - paddingRight;
          
          let displayCount = slidesToShow;
          if (isMobile) {
            displayCount = Math.min(slidesToShow, 1);
          } else if (isTablet) {
            displayCount = Math.min(slidesToShow, 2);
          }
          
          const exactSlideWidth = innerWidth / displayCount;
          setSlideWidth(exactSlideWidth);
          
          if (infiniteSlide) {
            setCurrentIndex(slidesToShow);
            setTranslateX(-exactSlideWidth * slidesToShow);
          } else {
            setCurrentIndex(0);
            setTranslateX(0);
          }
        } catch (error) {
          console.error('슬라이드 너비 계산 오류:', error);
        }
      };
      
      calculateWidths();
      window.addEventListener('resize', calculateWidths);
      
      return () => {
        window.removeEventListener('resize', calculateWidths);
      };
    }
  }, [containerRef, extendedImages.length, slidesToShow, isMobile, isTablet, infiniteSlide]);

  // Infinite slide boundary handling effect
  useEffect(() => {
    if (!infiniteSlide || !extendedImages || extendedImages.length === 0 || slideWidth === 0) return;
    
    const handleTransitionEnd = () => {
      if (currentIndex <= 0) {
        setIsTransitioning(false);
        const newIndex = images.length;
        setCurrentIndex(newIndex);
        setTranslateX(-newIndex * slideWidth);
      } else if (currentIndex >= images.length + slidesToShow) {
        setIsTransitioning(false);
        const newIndex = slidesToShow;
        setCurrentIndex(newIndex);
        setTranslateX(-newIndex * slideWidth);
      }
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('transitionend', handleTransitionEnd);
      return () => slider.removeEventListener('transitionend', handleTransitionEnd);
    }
  }, [currentIndex, extendedImages, images, infiniteSlide, slidesToShow, slideWidth]);

  // Re-enable transition effect
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  // Auto slide management effect
  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
  }, [startAutoSlide, stopAutoSlide]);

  return {
    // Props
    sliderTitle,
    showTitle,
    titleFontSize,
    titleFontWeight,
    titleColor,
    sliderHeight,
    slidesToShow,
    autoSlide,
    autoSlideInterval,
    infiniteSlide,
    showNavigation,
    showIndicators,
    imageObjectFit,
    images,
    
    // State
    currentIndex,
    translateX,
    slideWidth,
    isTransitioning,
    isEditingTitle,
    titleEditValue,
    touchStart,
    touchEnd,
    isMobile,
    isTablet,
    extendedImages,
    
    // Refs
    sliderRef,
    containerRef,
    autoSlideTimer,
    
    // Computed styles
    titleStyle,
    sliderContainerStyle,
    sliderStyle,
    
    // Functions
    goToNext,
    goToPrev,
    goToSlide,
    startAutoSlide,
    stopAutoSlide,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handleTitleDoubleClick,
    handleTitleBlur,
    handleImageClick,
    setTitleEditValue,
    setIsTransitioning,
    setCurrentIndex,
    setTranslateX,
    setSlideWidth,
    
    // Utils
    t,
    dispatch,
    id,
    data,
    style,
    className,
    swipeThreshold
  };
};